
import React, { useState, useContext, useMemo } from 'react';
// FIX: Changed import to correct PageContext file to avoid circular dependency
import { PageContext } from '../PageContext';
import { Page, UserProfile } from '../types';
import { isUserOnline } from '../utils';
import { useAuth } from '../AuthContext';
import { useAdmin } from '../AdminContext';
import BackButton from '../components/BackButton';
import { Capacitor } from '@capacitor/core';

interface InviteFriendsPageProps {
    groupId: string;
}

const InviteFriendsPage: React.FC<InviteFriendsPageProps> = ({ groupId }) => {
    const { userProfile, allUsers, groups } = useAuth();
    const { sendDirectNotification } = useAdmin();
    const pageContext = useContext(PageContext);
    const [searchTerm, setSearchTerm] = useState('');
    const [invitedUsers, setInvitedUsers] = useState<Set<string>>(new Set());

    const group = useMemo(() => groups.find(g => g.id === groupId), [groups, groupId]);

    const filteredUsers = useMemo(() => {
        if (!userProfile) return [];

        const isNotBlocked = (u: UserProfile) => {
            const iHaveBlockedThem = userProfile.blockedUsers?.includes(u.uid);
            const theyHaveBlockedMe = u.blockedUsers?.includes(userProfile.uid);
            return !iHaveBlockedThem && !theyHaveBlockedMe;
        };

        if (!searchTerm) {
            // Show friends by default (mutual follows) when search is empty
            return allUsers.filter(u => 
                userProfile.following.includes(u.uid) && 
                userProfile.followers.includes(u.uid) &&
                isNotBlocked(u)
            );
        }

        // Search global users
        const lowerTerm = searchTerm.toLowerCase();
        return allUsers.filter(u => 
            u.uid !== userProfile.uid &&
            u.displayName.toLowerCase().includes(lowerTerm) &&
            isNotBlocked(u)
        );
    }, [searchTerm, allUsers, userProfile]);

    const handleShare = async () => {
        if (!group || !userProfile) return;
        
        const code = group.inviteCode || group.id;
        
        // SMART LINKING: Use live URL if on native device
        const isNative = Capacitor.isNativePlatform();
        const url = isNative 
            ? 'https://what-was-it-gc-fb-56459.web.app' 
            : window.location.origin;

        const text = `Join ${userProfile.displayName}'s group on What Was It? Join the fun! \nCode: ${code}`;

        if (navigator.share) {
            try {
                await navigator.share({
                    title: `Join ${group.name}`,
                    text: text,
                    url: url,
                });
            } catch (err) {
                console.log('Share cancelled or failed', err);
            }
        } else {
            navigator.clipboard.writeText(`${text}\n${url}`);
            alert('Invite message copied to clipboard!');
        }
    };

    const handleInvite = (user: UserProfile) => {
        if (!group || !userProfile) return;
        
        // Prevent double invites in session
        if (invitedUsers.has(user.uid)) return;

        sendDirectNotification(user.uid, {
            type: 'group_invite',
            text: `invited you to join the group "${group.name}"`,
            fromUser: {
                id: userProfile.uid,
                name: userProfile.displayName,
                avatar: userProfile.photoURL,
            },
            group: {
                id: group.id,
                name: group.name
            }
        });

        setInvitedUsers(prev => new Set(prev).add(user.uid));
    };

    const handleDone = () => {
        if(group) {
            pageContext?.switchPage(Page.GameDetail, group.name, { gameId: group.id });
        } else {
            pageContext?.switchPage(Page.Groups, 'Groups');
        }
    };

    if (!group) return <div className="text-white text-center pt-10">Group not found.</div>;

    const canShare = typeof navigator.share !== 'undefined';

    return (
        <div className="bg-[var(--primary-dark-purple)] min-h-full -mt-4 -mx-4 flex flex-col">
            {/* Header */}
            <div className="p-4 flex items-center justify-between">
                <button onClick={handleDone} className="text-white font-bold flex items-center hover:opacity-80 transition-opacity">
                    <i className="ph-duotone ph-arrow-left mr-1 text-xl"></i> Back to Group
                </button>
                <h1 className="text-xl font-bold text-white aurora-text">Invite Users</h1>
                <div className="w-10"></div> {/* Spacer to balance header */}
            </div>

            <div className="p-4 space-y-6 flex-grow overflow-y-auto">
                
                {/* Invite Section */}
                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 text-center border border-white/20 shadow-lg">
                    <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-md">
                        <i className="ph-duotone ph-paper-plane-tilt text-3xl text-white"></i>
                    </div>
                    <h2 className="text-2xl font-bold text-white mb-1">Invite Others</h2>
                    <p className="text-gray-300 text-sm mb-4">Share the group code with anyone.</p>
                    
                    <button 
                        onClick={handleShare} 
                        className="w-full bg-white text-[var(--primary-dark-purple)] font-bold py-3 px-4 rounded-xl shadow-lg flex items-center justify-center gap-2 hover:bg-gray-100 transition-colors"
                    >
                        <i className={`ph-bold ${canShare ? 'ph-share-network' : 'ph-copy'} text-xl`}></i>
                        {canShare ? 'Share Invite' : 'Copy Invite Message'}
                    </button>
                    <p className="text-xs text-gray-400 mt-2">
                        {canShare ? 'Tap to share via message, email, or social media' : 'Copies invite text and code to clipboard'}
                    </p>
                </div>

                {/* User List Section */}
                <div className="space-y-4">
                    <h3 className="text-lg font-bold text-white border-b border-white/10 pb-2">
                        {searchTerm ? 'Search Results' : 'Invite Friends'}
                    </h3>
                    
                    <div className="relative">
                        <i className="ph-duotone ph-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                        <input
                            type="text"
                            placeholder="Search for anyone..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full bg-black/20 border border-white/10 rounded-xl py-3 pl-10 pr-4 text-white placeholder-gray-400 focus:outline-none focus:border-purple-500"
                        />
                    </div>

                    <div className="space-y-2">
                        {filteredUsers.length > 0 ? (
                            filteredUsers.map(user => {
                                const isMember = group.members.includes(user.uid);
                                const isInvited = invitedUsers.has(user.uid);

                                return (
                                    <div key={user.uid} className="flex items-center justify-between bg-white/5 p-3 rounded-xl hover:bg-white/10 transition-colors">
                                        <div className="flex items-center space-x-3">
                                            <div className="relative">
                                                <img src={user.photoURL} alt={user.displayName} className="w-10 h-10 rounded-full object-cover" />
                                                {isUserOnline(user) && <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-gray-800 rounded-full"></span>}
                                            </div>
                                            <div className="text-white">
                                                <p className="font-bold text-sm">{user.displayName}</p>
                                                <p className="text-xs text-gray-400">@{user.displayName.toLowerCase().replace(/\s/g, '')}</p>
                                            </div>
                                        </div>
                                        
                                        {isMember ? (
                                            <span className="text-gray-400 text-xs font-bold px-3 py-1">Member</span>
                                        ) : isInvited ? (
                                            <span className="text-yellow-400 text-xs font-bold px-3 py-1 flex items-center gap-1">
                                                <i className="ph-fill ph-check-circle"></i> Sent
                                            </span>
                                        ) : (
                                            <button 
                                                onClick={() => handleInvite(user)}
                                                className="bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-bold px-4 py-2 rounded-lg shadow-md hover:opacity-90 active:scale-95 transition-all"
                                            >
                                                Invite
                                            </button>
                                        )}
                                    </div>
                                );
                            })
                        ) : (
                            <div className="text-center py-8 text-gray-400">
                                <p>No users found.</p>
                                <p className="text-xs mt-1">Try a different search term.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InviteFriendsPage;
